<template>
  <el-select
    :model-value="modelValue"
    filterable
    remote
    reserve-keyword
    clearable
    :remote-method="scheduleSearch"
    :loading="loading"
    :placeholder="placeholder"
    :disabled="disabled"
    :class="selectClass"
    :no-data-text="noDataText"
    popper-class="live-id-select-dropdown"
    @update:model-value="onUpdate"
    @visible-change="onVisible"
  >
    <el-option
      v-for="item in options"
      :key="item.id"
      :label="labelOf(item)"
      :value="item.id!"
    >
      <div class="live-id-option">
        <span class="live-id-option__name">{{ item.nickname || item.name || '未命名用户' }}</span>
        <span class="live-id-option__id">编号 {{ item.id }}</span>
      </div>
    </el-option>
  </el-select>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { getUser, getUserPage, type UserVO } from '@/api/member/user'

const props = withDefaults(
  defineProps<{
    modelValue?: number | string | null
    placeholder?: string
    disabled?: boolean
    selectClass?: string
  }>(),
  {
    placeholder: '输入昵称 / 手机号 / 编号搜索，点选下方列表',
    disabled: false,
    selectClass: '!w-240px'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void
  (e: 'change', value: number | undefined, user?: UserVO): void
}>()

const loading = ref(false)
const options = ref<UserVO[]>([])
const noDataText = ref('暂无数据，请输入关键字搜索')
let debounceTimer: ReturnType<typeof setTimeout> | undefined
let searchSeq = 0

const toNum = (v: number | string | null | undefined) => {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

const labelOf = (item: UserVO) => {
  const name = item.nickname || item.name || '未命名用户'
  return `${name}（${item.id}）`
}

const ensureOption = async (id?: number) => {
  if (!id) return
  if (options.value.some((item) => item.id === id)) return
  try {
    const one: any = await getUser(id)
    if (one?.id) {
      options.value = [one, ...options.value.filter((x) => x.id !== one.id)]
      return
    }
  } catch {
    /* fallback */
  }
  options.value = [{ id, nickname: `用户 ${id}` } as UserVO, ...options.value]
}

const search = async (keyword?: string) => {
  const seq = ++searchSeq
  loading.value = true
  noDataText.value = '暂无数据，请输入关键字搜索'
  try {
    const text = String(keyword || '').trim()
    const map = new Map<number, UserVO>()
    if (/^\d+$/.test(text)) {
      try {
        const one: any = await getUser(Number(text))
        if (one?.id) map.set(one.id, one)
      } catch {
        /* continue */
      }
      try {
        const byId: any = await getUserPage({ pageNo: 1, pageSize: 20, id: Number(text) })
        for (const row of byId.list || []) {
          if (row?.id != null) map.set(row.id, row)
        }
      } catch {
        /* ignore */
      }
      if (map.size === 0) {
        const fallback: any = await getUserPage({ pageNo: 1, pageSize: 20 })
        for (const row of fallback.list || []) {
          if (row?.id != null) map.set(row.id, row)
        }
        noDataText.value = `未找到用户 ${text}，可从下列会员中选择或改用昵称搜索`
      }
    } else {
      const params: Record<string, any> = { pageNo: 1, pageSize: 20 }
      if (/^1\d{10}$/.test(text)) {
        params.mobile = text
      } else if (text) {
        params.nickname = text
      }
      const data: any = await getUserPage(params)
      for (const row of data.list || []) {
        if (row?.id != null) map.set(row.id, row)
      }
    }
    if (seq !== searchSeq) return
    options.value = Array.from(map.values())
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

const scheduleSearch = (keyword?: string) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    void search(keyword)
  }, 200)
}

const onUpdate = (val: number | undefined) => {
  const id = toNum(val)
  emit('update:modelValue', id)
  const user = id == null ? undefined : options.value.find((item) => item.id === id)
  emit('change', id, user)
}

const onVisible = async (open: boolean) => {
  if (open) await search('')
}

watch(
  () => props.modelValue,
  async (val) => {
    const id = toNum(val)
    if (id) await ensureOption(id)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

defineExpose({ search, ensureOption })
</script>

<style scoped>
.live-id-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.live-id-option__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-id-option__id {
  flex-shrink: 0;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
</style>
